import { RadioGroup, FormControlLabel, Radio } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { Environment } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { Environments } from '@/data/inventory/data';

function getUnifiedEnvironment(entities: InventoryResource[]) {
  const environment =
    entities[0].classifications?.environmentDetails?.environment;
  if (
    entities.every(
      (entity) =>
        entity.classifications?.environmentDetails?.environment === environment
    )
  ) {
    return environment;
  }

  return null;
}

export function EnvironmentEditor({
  open,
  onClose,
  entities,
}: {
  open: boolean;
  onClose: () => void;
  entities: InventoryResource[];
}) {
  const { snackbar } = useWorkspace();
  const [environment, setEnvironment] = useState(
    getUnifiedEnvironment(entities)
  );
  const dal = useDAL();
  const [updating, setUpdating] = useState(false);

  const onSave = () => {
    if (!environment) return;
    setUpdating(true);
    void Promise.all(
      entities.map((entity) =>
        dal.inventory.environments.override(entity.id, environment)
      )
    )
      .then(() => {
        snackbar.showMessage(
          `Environment overridden for ${entities.map((x) => x.resourceName).join(', ')}.`
        );
      })
      .finally(() => {
        setUpdating(false);
        onClose();
      });
  };
  return (
    <MainDialog
      header={{
        title: 'Override environment',
        subtitle: `Eon won't automatically classify environment for the selected resources. This could change the applied backup policies and controls.`,
        onClose,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'override-button',
          text: 'Override',
          onClick: onSave,
          disabled: !environment || updating,
        },
        secondaryButton: {
          'data-testid': 'cancel-button',
          text: 'Cancel',
          onClick: onClose,
        },
      }}
      isOpen={open}
      isLoading={false}
    >
      <RadioGroup>
        {Object.values(Environment)
          .filter((x) => x !== Environment.EnvironmentUnspecified)
          .map((x) => (
            <div key={x}>
              <FormControlLabel
                title={Environments[x].title}
                className='w-full'
                control={
                  <Radio
                    sx={{ padding: '10px 10px 10px 30px' }}
                    size='small'
                    checked={x === environment}
                    onChange={() => {
                      setEnvironment(x);
                    }}
                  />
                }
                label={Environments[x].title}
              />
            </div>
          ))}
      </RadioGroup>
    </MainDialog>
  );
}
